// Pipe data that travels with its object (engine, 2026-10-06), the same way meta.pivot does
// (src/tools/pivot.ts carryPivot / shiftPivotInPlace):
//   - meta.ports on a fitting: the open ends (points AND directions).
//   - meta.pipe on a pipe: its FREE anchors (attached anchors follow their owner instead).
//   - meta.pipeRef on a pipe: the port frame each ATTACHED end was last built against. The
//     rebuild compares it with the port now; a difference means the owner moved or turned,
//     and the pipe's free end swings with it (see api.ts rebuildPatches). An entry is carried
//     only when its owner moves in the same operation (then nothing swings twice).
import { normalize, type Vec } from "../core/geometry";
import type { Entity } from "../doc/entities";
import type { FittingPortData, PipeAnchor, PipeEndStyle, PipeMeta } from "./api";

type Patch = { id: string; before: Record<string, unknown>; after: Record<string, unknown> };

/** One attached end's port frame at the last rebuild. */
export interface PipeRefEnd {
  ownerId: string;
  port: number;
  p: Vec;
  dir: Vec;
}
export interface PipeRef {
  a?: PipeRefEnd;
  b?: PipeRefEnd;
}

const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
export const isVec = (v: unknown): v is Vec => !!v && typeof v === "object" && isNum((v as Vec).x) && isNum((v as Vec).y);

const END_STYLES = new Set<PipeEndStyle>(["none", "flat", "circle", "flex"]);

function anchorOk(v: unknown): v is PipeAnchor {
  if (!v || typeof v !== "object") return false;
  const a = v as Record<string, unknown>;
  if (a.kind === "free") return isVec(a.p);
  if (a.kind === "port") return typeof a.ownerId === "string" && Number.isInteger(a.port) && (a.port as number) >= 0;
  return false;
}

/** meta.pipe when it is well formed, else null (no group / child checks here). */
export function rawPipeMeta(entity: Entity | undefined): PipeMeta | null {
  if (!entity || entity.type !== "group") return null;
  const m = entity.meta?.pipe as Record<string, unknown> | undefined;
  if (!m || typeof m !== "object") return null;
  if (!isNum(m.size) || !anchorOk(m.a) || !anchorOk(m.b)) return null;
  if (!END_STYLES.has(m.endA as PipeEndStyle) || !END_STYLES.has(m.endB as PipeEndStyle)) return null;
  return m as unknown as PipeMeta;
}

/** meta.ports when it is well formed (a non-empty list), else null. */
export function rawPorts(entity: Entity | undefined): FittingPortData[] | null {
  const list = entity?.meta?.ports;
  if (!Array.isArray(list) || list.length === 0) return null;
  for (const pt of list) {
    if (!pt || typeof pt !== "object" || !isVec(pt.p) || !isVec(pt.dir) || !isNum(pt.size)) return null;
  }
  return list as FittingPortData[];
}

function refEndOk(v: unknown): v is PipeRefEnd {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return typeof r.ownerId === "string" && Number.isInteger(r.port) && isVec(r.p) && isVec(r.dir);
}

export function pipeRefOf(entity: Entity | undefined): PipeRef {
  const r = entity?.meta?.pipeRef as Record<string, unknown> | undefined;
  const out: PipeRef = {};
  if (r && typeof r === "object") {
    if (refEndOk(r.a)) out.a = r.a;
    if (refEndOk(r.b)) out.b = r.b;
  }
  return out;
}

/** A direction under a point map: normalize(mp(p + dir) - mp(p)); unchanged if that collapses. */
export function mapDir(mp: (p: Vec) => Vec, p: Vec, dir: Vec): Vec {
  const q0 = mp(p);
  const q1 = mp({ x: p.x + dir.x, y: p.y + dir.y });
  const n = normalize({ x: q1.x - q0.x, y: q1.y - q0.y });
  return n.x === 0 && n.y === 0 ? { ...dir } : n;
}

/**
 * The meta keys of `entity` that change under the point map `mp`, or null when it carries no
 * pipe data. `moving` = every id moved by the same operation (groups expanded); a pipeRef entry
 * is carried only when its owner is in it. Without `moving` (in-place clone shift) all move.
 */
export function movedPipeMeta(entity: Entity, mp: (p: Vec) => Vec, moving?: ReadonlySet<string>): Record<string, unknown> | null {
  if (entity.type !== "group") return null;
  const out: Record<string, unknown> = {};
  const ports = rawPorts(entity);
  // spread first: a port's other fields (3.15.6 `vertical`) travel with it
  if (ports) out.ports = ports.map((pt) => ({ ...pt, p: mp(pt.p), dir: mapDir(mp, pt.p, pt.dir), size: pt.size }));
  const pipe = rawPipeMeta(entity);
  if (pipe) {
    const mv = (a: PipeAnchor): PipeAnchor => (a.kind === "free" ? { kind: "free", p: mp(a.p) } : { ...a });
    if (pipe.a.kind === "free" || pipe.b.kind === "free") out.pipe = { ...pipe, a: mv(pipe.a), b: mv(pipe.b) };
    const ref = pipeRefOf(entity);
    const next: PipeRef = { ...ref };
    let refMoved = false;
    for (const k of ["a", "b"] as const) {
      const r = ref[k];
      if (!r || (moving && !moving.has(r.ownerId))) continue;
      next[k] = { ...r, p: mp(r.p), dir: mapDir(mp, r.p, r.dir) };
      refMoved = true;
    }
    if (refMoved) out.pipeRef = next;
  }
  return Object.keys(out).length > 0 ? out : null;
}

/** Add the moved ports / free pipe anchors to an entity's patch (as carryPivot does). */
export function carryPipe(entity: Entity, patch: Patch | null, mp: (p: Vec) => Vec, moving?: ReadonlySet<string>): Patch | null {
  const changes = movedPipeMeta(entity, mp, moving);
  if (!changes) return patch;
  const out = patch ?? { id: entity.id, before: {}, after: {} };
  const metaAfter = (out.after.meta as Record<string, unknown> | undefined) ?? entity.meta ?? {};
  out.before.meta = entity.meta;
  out.after.meta = { ...metaAfter, ...changes };
  return out;
}

/** In-place version for clones (paste, duplicate, library placement and saving). */
export function shiftPipeInPlace(entity: Entity, d: Vec): void {
  const changes = movedPipeMeta(entity, (p) => ({ x: p.x + d.x, y: p.y + d.y }));
  if (changes) entity.meta = { ...(entity.meta ?? {}), ...changes };
}

/**
 * Paste / duplicate (call BEFORE the clone is shifted): an attached end whose owner is pasted
 * too follows the new copy of that owner; an end whose owner stays behind becomes a FREE end
 * where it is now (so the copy does not jump back), and a joined ("none") end is cut "flat".
 * `src` is the clipboard entity, `clip` every clipboard entity by id, `idMap` old → new ids.
 */
export function remapPipeForPaste(src: Entity, clone: Entity, clip: ReadonlyMap<string, Entity>, idMap: ReadonlyMap<string, string>): void {
  const pipe = rawPipeMeta(src);
  if (!pipe || src.type !== "group" || clone.type !== "group") return;
  const centre = clip.get(src.children[2] ?? "");
  const ref = pipeRefOf(src);
  const next: PipeMeta = structuredClone(pipe);
  const nextRef: PipeRef = {};
  for (const k of ["a", "b"] as const) {
    const anchor = pipe[k];
    if (anchor.kind !== "port") continue;
    const newOwner = idMap.get(anchor.ownerId);
    const styleKey = k === "a" ? "endA" : "endB";
    if (newOwner) {
      next[k] = { kind: "port", ownerId: newOwner, port: anchor.port };
      const r = ref[k];
      if (r && r.ownerId === anchor.ownerId) nextRef[k] = { ...r, ownerId: newOwner };
      continue;
    }
    let p: Vec | null = null;
    if (centre?.type === "polyline" && centre.points.length >= 2) p = k === "a" ? centre.points[0]! : centre.points[centre.points.length - 1]!;
    else if (ref[k]) p = ref[k]!.p;
    if (!p) continue; // left for the deriver, which frees it at the drawn end
    next[k] = { kind: "free", p: { x: p.x, y: p.y } };
    if (next[styleKey] === "none") next[styleKey] = "flat";
  }
  const meta: Record<string, unknown> = { ...(clone.meta ?? {}), pipe: next };
  if (nextRef.a || nextRef.b) meta.pipeRef = nextRef;
  else delete meta.pipeRef;
  clone.meta = meta;
}
